import React, { useState, useEffect, useMemo } from 'react';
import { useDatepicker } from '../hooks/useDatepicker';
import { t } from '../utils/i18n';
import { toSomaliDept } from '../utils/deptTranslations';

const AdminApplyLeave = ({ api }) => {
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [armyPersonnel, setArmyPersonnel] = useState([]);
  const [armyTypes, setArmyTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingEntitlements, setLoadingEntitlements] = useState(false);
  const [selectedPersonnelDetails, setSelectedPersonnelDetails] = useState(null);
  const [leaveEntitlements, setLeaveEntitlements] = useState([]);
  const [takenMap, setTakenMap] = useState({});

  // Filter states
  const [filterArmyType, setFilterArmyType] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterSubDepartment, setFilterSubDepartment] = useState('');
  const [filterOrganization, setFilterOrganization] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Form data
  const [formData, setFormData] = useState({
    army_personnel_id: '',
    leave_type_id: '',
    start_date: '',
    end_date: '',
    apply_leave_start_date: '',
    apply_leave_end_date: '',
    reason: ''
  });
  const [selectedPersonnelName, setSelectedPersonnelName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState('');

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setSelectedFile(null);
      setSelectedFileName('');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('File size exceeds 10MB limit');
      e.target.value = '';
      return;
    }
    setSelectedFileName(file.name);
    const reader = new FileReader();
    reader.onloadend = () => {
      setSelectedFile(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleDateChange = (fieldName, value) => {
    setFormData(prev => {
      const updated = { ...prev, [fieldName]: value };
      if (fieldName === 'apply_leave_start_date') updated.start_date = value;
      if (fieldName === 'apply_leave_end_date') updated.end_date = value;
      return updated;
    });
  };
  useDatepicker(handleDateChange);

  const parseSelectedDate = (dateStr) => {
    if (!dateStr) return null;
    let parts;
    if (dateStr.includes('/')) {
      parts = dateStr.split('/');
      if (parts.length === 3) {
        const [m, d, y] = parts;
        return new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
      }
    } else if (dateStr.includes('-')) {
      parts = dateStr.split('-');
      if (parts.length === 3) {
        const [y, m, d] = parts;
        return new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
      }
    }
    return null;
  };

  const calculatedDuration = useMemo(() => {
    const startDateInput = document.querySelector('input[name="apply_leave_start_date"]');
    const endDateInput = document.querySelector('input[name="apply_leave_end_date"]');
    const startStr = formData.apply_leave_start_date || formData.start_date || startDateInput?.value;
    const endStr = formData.apply_leave_end_date || formData.end_date || endDateInput?.value;

    const sDate = parseSelectedDate(startStr);
    const eDate = parseSelectedDate(endStr);

    if (sDate && eDate) {
      if (eDate < sDate) {
        return { isValid: false, days: 0, message: 'To Date cannot be prior to From Date' };
      }
      const diffTime = eDate.getTime() - sDate.getTime();
      const diffDays = Math.round(diffTime / (1000 * 3600 * 24)) + 1;
      return { isValid: true, days: diffDays, message: `Total Leave Duration: ${diffDays} Day${diffDays > 1 ? 's' : ''}` };
    }
    return null;
  }, [formData.apply_leave_start_date, formData.apply_leave_end_date, formData.start_date, formData.end_date]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const leaveTypesRes = await api.get('/leave_types');
      setLeaveTypes(leaveTypesRes?.data?.leave_types || []);

      const armyTypesRes = await api.get('/army_types');
      setArmyTypes(armyTypesRes || []);

      const armyPersonnelRes = await api.get('/leave_requests/personnel_list');
      setArmyPersonnel(armyPersonnelRes?.data?.personnel || []);

      const departmentsRes = await api.get('/departments');
      setDepartments(departmentsRes || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get unique values for filters
  const uniqueOrganizations = useMemo(() => {
    return [...new Set(armyPersonnel.map(p => p.organization).filter(Boolean))].sort();
  }, [armyPersonnel]);

  // Get main departments (no parent)
  const mainDepartments = useMemo(() => {
    return departments.filter(d => !d.parent_id);
  }, [departments]);

  // Get sub-departments (have parent)
  const subDepartmentsList = useMemo(() => {
    return departments.filter(d => d.parent_id);
  }, [departments]);

  // Get departments filtered by selected army type with display name deduplication
  const filteredDepartments = useMemo(() => {
    let depts = filterArmyType
      ? mainDepartments.filter(d => String(d.army_type_id) === String(filterArmyType))
      : mainDepartments;
    const seenNames = new Set();
    return depts.filter(d => {
      const displayName = toSomaliDept(d.name).trim();
      if (!displayName || seenNames.has(displayName)) return false;
      seenNames.add(displayName);
      return true;
    });
  }, [mainDepartments, filterArmyType]);

  // Get sub-departments filtered by selected department / army type with display name deduplication
  const filteredSubDepartments = useMemo(() => {
    let subDepts = [];
    if (filterDepartment) {
      const selectedDept = departments.find(d => String(d.id) === String(filterDepartment));
      if (selectedDept) {
        const matchingDeptIds = new Set(
          departments
            .filter(d =>
              d.name.trim() === selectedDept.name.trim() &&
              (!filterArmyType || String(d.army_type_id) === String(filterArmyType))
            )
            .map(d => String(d.id))
        );
        subDepts = subDepartmentsList.filter(sd => matchingDeptIds.has(String(sd.parent_id)));
      } else {
        subDepts = subDepartmentsList.filter(sd => String(sd.parent_id) === String(filterDepartment));
      }
    } else if (filterArmyType) {
      const validDeptIds = new Set(
        mainDepartments
          .filter(d => String(d.army_type_id) === String(filterArmyType))
          .map(d => String(d.id))
      );
      subDepts = subDepartmentsList.filter(sd =>
        validDeptIds.has(String(sd.parent_id)) || String(sd.army_type_id) === String(filterArmyType)
      );
    } else {
      subDepts = subDepartmentsList;
    }

    const seenNames = new Set();
    return subDepts.filter(sd => {
      const displayName = toSomaliDept(sd.name).trim();
      if (!displayName || seenNames.has(displayName)) return false;
      seenNames.add(displayName);
      return true;
    });
  }, [subDepartmentsList, mainDepartments, departments, filterDepartment, filterArmyType]);

  const filteredPersonnel = useMemo(() => {
    return armyPersonnel.filter(p => {
      const selectedArmyType = armyTypes.find(at => String(at.id) === String(filterArmyType));
      const matchesArmyType = !filterArmyType ||
        String(p.army_type_id) === String(filterArmyType) ||
        (p.army_type && selectedArmyType && p.army_type.toLowerCase() === selectedArmyType.name.toLowerCase());

      const selectedDepartment = departments.find(d => String(d.id) === String(filterDepartment));
      const matchesDepartment = !filterDepartment ||
        String(p.department_id) === String(filterDepartment) ||
        (p.department && selectedDepartment && p.department.toLowerCase() === selectedDepartment.name.toLowerCase());

      const selectedSubDept = departments.find(d => String(d.id) === String(filterSubDepartment));
      const matchesSubDepartment = !filterSubDepartment ||
        String(p.sub_department_id) === String(filterSubDepartment) ||
        (p.sub_department && selectedSubDept && p.sub_department.toLowerCase() === selectedSubDept.name.toLowerCase());

      const matchesOrganization = !filterOrganization || p.organization === filterOrganization;
      const matchesSearch = !searchTerm ||
        String(p.full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(p.rank || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(p.army_id || '').toLowerCase().includes(searchTerm.toLowerCase());

      return matchesArmyType && matchesDepartment && matchesSubDepartment && matchesOrganization && matchesSearch;
    });
  }, [armyPersonnel, filterArmyType, filterDepartment, filterSubDepartment, filterOrganization, searchTerm, armyTypes, departments]);

  const fetchPersonnelLeaveDetails = async (personnelId) => {
    setLoadingEntitlements(true);
    try {
      const entitlementsRes = await api.get(`/leave_entitlements?army_personnel_id=${personnelId}`);
      const entitlements = entitlementsRes?.data?.leave_entitlements || [];
      setLeaveEntitlements(entitlements);

      try {
        const leavesRes = await api.get(`/leave_requests?army_personnel_id=${personnelId}&status=approved&per_page=10000`);
        const leaves = (leavesRes?.data && leavesRes.data.leave_requests) || [];
        const map = {};
        leaves.forEach(l => {
          const year = new Date(l.start_date).getFullYear();
          const leaveTypeName = (l.leave_type && (l.leave_type.name || l.leave_type)) || l.leave_type_name || '';
          const key = `${leaveTypeName}-${year}`;
          const days = (new Date(l.end_date) - new Date(l.start_date)) / (1000 * 60 * 60 * 24) + 1;
          map[key] = (map[key] || 0) + Math.max(0, Math.round(days));
        });
        setTakenMap(map);
      } catch (err) {
        console.error('Error fetching approved leaves:', err);
        setTakenMap({});
      }
    } catch (error) {
      console.error('Error fetching leave details:', error);
      setLeaveEntitlements([]);
    } finally {
      setLoadingEntitlements(false);
    }
  };

  const handleSelectPersonnel = (person) => {
    setFormData({ ...formData, army_personnel_id: person.id });
    const deptDisplay = person.department || 'N/A';
    const subDeptDisplay = person.sub_department ? ` • ${person.sub_department}` : '';
    const orgDisplay = person.organization ? ` • ${person.organization}` : '';
    setSelectedPersonnelName(`${person.full_name} (${person.rank}) - ${deptDisplay}${subDeptDisplay}${orgDisplay}`);
    setSelectedPersonnelDetails(person);
    fetchPersonnelLeaveDetails(person.id);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const startDateInput = document.querySelector('input[name="apply_leave_start_date"]');
      const endDateInput = document.querySelector('input[name="apply_leave_end_date"]');

      const formatDate = (dateStr) => {
        if (!dateStr) return '';
        if (dateStr.includes('/')) {
          const [month, day, year] = dateStr.split('/');
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
        return dateStr;
      };

      const submitData = {
        army_personnel_id: formData.army_personnel_id,
        leave_type_id: formData.leave_type_id,
        start_date: formatDate(startDateInput?.value || formData.start_date),
        end_date: formatDate(endDateInput?.value || formData.end_date),
        reason: formData.reason,
        document_data: selectedFile,
        document_name: selectedFileName
      };

      if (!submitData.army_personnel_id) {
        alert(t('please_select_personnel', 'Please select a personnel'));
        setLoading(false);
        return;
      }
      if (!submitData.leave_type_id) {
        alert(t('please_select_leave_type', 'Please select a leave type'));
        setLoading(false);
        return;
      }
      if (!submitData.start_date || !submitData.end_date) {
        alert(t('please_select_dates', 'Please select both from and to dates'));
        setLoading(false);
        return;
      }
      if (!submitData.reason) {
        alert(t('please_enter_reason', 'Please enter a reason'));
        setLoading(false);
        return;
      }

      const startDate = new Date(submitData.start_date);
      const endDate = new Date(submitData.end_date);
      if (endDate < startDate) {
        alert(t('end_date_error', 'To Date cannot be prior to From Date'));
        setLoading(false);
        return;
      }

      const { response, data } = await api.post('/leave_requests', { leave_request: submitData });
      if (response.ok) {
        alert(t('leave_request_submitted', 'Leave request submitted successfully'));
        resetForm();
        if (startDateInput) startDateInput.value = '';
        if (endDateInput) endDateInput.value = '';
      } else {
        const errorMessages = [];
        if (data && data.errors) {
          errorMessages.push(Array.isArray(data.errors) ? data.errors.join('\n') : data.errors);
        }
        if (data && data.error) {
          errorMessages.push(Array.isArray(data.error) ? data.error.join('\n') : data.error);
        }
        alert(errorMessages.filter(Boolean).join('\n\n') || (t('failed_create_leave', 'Failed to create leave request')));
      }
    } catch (error) {
      console.error('Failed to save leave request:', error);
      alert((t('failed_save_leave', 'Failed to save leave request')) + ': ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      army_personnel_id: '',
      leave_type_id: '',
      start_date: '',
      end_date: '',
      apply_leave_start_date: '',
      apply_leave_end_date: '',
      reason: ''
    });
    setSearchTerm('');
    setSelectedPersonnelName('');
    setSelectedPersonnelDetails(null);
    setLeaveEntitlements([]);
    setTakenMap({});
    setFilterArmyType('');
    setFilterDepartment('');
    setFilterSubDepartment('');
    setFilterOrganization('');
    setSelectedFile(null);
    setSelectedFileName('');
    const startDateInput = document.querySelector('input[name="apply_leave_start_date"]');
    const endDateInput = document.querySelector('input[name="apply_leave_end_date"]');
    const fileInput = document.querySelector('input[type="file"]');
    if (startDateInput) startDateInput.value = '';
    if (endDateInput) endDateInput.value = '';
    if (fileInput) fileInput.value = '';
  };

  return (
    <div className="baseTableFormSec">
      <div className="dFlex baseTableHd mb_16">
        <h3 className="txt20 fwMedium colorBlack">{t('apply_leave_personnel', 'Apply Leave for Personnel')}</h3>
      </div>

      <form onSubmit={handleSubmit} className="baseTableInpts">
        {/* Filter Section */}
        <div style={{ backgroundColor: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '16px', marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: 600, color: '#333' }}>
            {t('filter_personnel', 'Filter Personnel')}
          </h4>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '12px' }}>
            {/* Army Type Filter */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '6px', color: '#555' }}>
                {t('army_type', 'Force')}
              </label>
              <select
                value={filterArmyType}
                onChange={(e) => {
                  setFilterArmyType(e.target.value);
                  setFilterDepartment('');
                  setFilterSubDepartment('');
                }}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '13px',
                  backgroundColor: '#fff'
                }}
              >
                <option value="">All Forces</option>
                {armyTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>

            {/* Department Filter */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '6px', color: '#555' }}>
                {t('department', 'Department')}
              </label>
              <select
                value={filterDepartment}
                onChange={(e) => {
                  setFilterDepartment(e.target.value);
                  setFilterSubDepartment('');
                }}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '13px',
                  backgroundColor: '#fff'
                }}
              >
                <option value="">All Departments</option>
                {filteredDepartments.map(d => (
                  <option key={d.id} value={d.id}>{toSomaliDept(d.name)}</option>
                ))}
              </select>
            </div>

            {/* Sub Department Filter */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '6px', color: '#555' }}>
                {t('sub_department', 'Sub Department')}
              </label>
              <select
                value={filterSubDepartment}
                onChange={(e) => setFilterSubDepartment(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '13px',
                  backgroundColor: '#fff'
                }}
              >
                <option value="">All Sub Departments</option>
                {filteredSubDepartments.map(sd => (
                  <option key={sd.id} value={sd.id}>{toSomaliDept(sd.name)}</option>
                ))}
              </select>
            </div>

            {/* Organization Filter */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '6px', color: '#555' }}>
                {t('organization', 'Organization Unit')}
              </label>
              <select
                value={filterOrganization}
                onChange={(e) => setFilterOrganization(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '13px',
                  backgroundColor: '#fff'
                }}
              >
                <option value="">All Organizations</option>
                {uniqueOrganizations.map(org => (
                  <option key={org} value={org}>{org}</option>
                ))}
              </select>
            </div>

            {/* Search Filter */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '6px', color: '#555' }}>
                {t('search', 'Search')}
              </label>
              <input
                type="text"
                placeholder={t('search_name_rank_id', 'Search by Name, Rank, Army ID...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '13px',
                  backgroundColor: '#fff'
                }}
              />
            </div>
          </div>

          <div style={{ fontSize: '12px', color: '#666' }}>
            {t('showing', 'Showing')} <strong>{filteredPersonnel.length}</strong> {t('personnel', 'personnel')}
          </div>
        </div>

        {/* Personnel Selection */}
        <ul className="formFieldsWrap">
          <li className="w_1_1">
            <div className="formInpt">
              <label className="mb_10">{t('select_personnel', 'Select Personnel')}</label>
              <div style={{
                border: '1px solid #ddd',
                borderRadius: '4px',
                maxHeight: '300px',
                overflowY: 'auto',
                backgroundColor: '#fff'
              }}>
                {filteredPersonnel.length > 0 ? (
                  filteredPersonnel.map((person) => (
                    <div
                      key={person.id}
                      onClick={() => handleSelectPersonnel(person)}
                      style={{
                        padding: '16px 20px',
                        borderBottom: '1px solid #f0f2f5',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        backgroundColor: formData.army_personnel_id === person.id ? '#e3f2fd' : '#fff',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        position: 'relative'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = formData.army_personnel_id === person.id ? '#e3f2fd' : '#f8f9fa';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = formData.army_personnel_id === person.id ? '#e3f2fd' : '#fff';
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                          <span style={{ fontWeight: 600, fontSize: '15px', color: '#1a1f36' }}>
                            {person.full_name}
                          </span>
                          {person.rank && (
                            <span style={{
                              fontSize: '11px',
                              fontWeight: 600,
                              backgroundColor: '#e8f0fe',
                              color: '#1a73e8',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              textTransform: 'uppercase'
                            }}>
                              {person.rank}
                            </span>
                          )}
                        </div>

                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          columnGap: '24px',
                          rowGap: '6px',
                          fontSize: '13px',
                          color: '#4f566b'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ color: '#8792a2', fontWeight: 500 }}>{t('army_type', 'Force')}:</span>
                            <span style={{ fontWeight: 500, color: '#2d3748' }}>{person.army_type || 'N/A'}</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ color: '#8792a2', fontWeight: 500 }}>{t('department', 'Dept')}:</span>
                            <span style={{ fontWeight: 500, color: '#2d3748' }}>{person.department || 'N/A'}</span>
                          </div>
                          {person.sub_department && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{ color: '#8792a2', fontWeight: 500 }}>{t('sub_department', 'Sub-Dept')}:</span>
                              <span style={{ fontWeight: 500, color: '#2d3748' }}>{person.sub_department}</span>
                            </div>
                          )}
                          {person.organization && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{ color: '#8792a2', fontWeight: 500 }}>{t('organization', 'Org')}:</span>
                              <span style={{ fontWeight: 500, color: '#2d3748' }}>{person.organization}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {formData.army_personnel_id === person.id && (
                        <div style={{
                          backgroundColor: '#1a73e8',
                          color: '#fff',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          boxShadow: '0 2px 4px rgba(26,115,232,0.3)'
                        }}>
                          ✓
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                    {t('no_personnel_found', 'No personnel found matching your filters')}
                  </div>
                )}
              </div>
            </div>
          </li>

          {loadingEntitlements ? (
            <li className="w_1_1">
              <div style={{ textAlign: 'center', padding: '24px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                <div style={{ display: 'inline-block', width: '24px', height: '24px', border: '3px solid #1a73e8', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                <div style={{ fontSize: '13px', color: '#555', marginTop: '8px', fontWeight: 500 }}>Fetching leave entitlements...</div>
              </div>
            </li>
          ) : selectedPersonnelDetails && leaveEntitlements.length > 0 && (
            <li className="w_1_1">
              <div className="formInpt">
                <label className="mb_10">{t('leave_entitlements', 'Leave Entitlements')}</label>
                <div style={{
                  backgroundColor: '#f9f9f9',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  padding: '12px',
                  overflowX: 'auto'
                }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '13px'
                  }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #ddd' }}>
                        <th style={{ textAlign: 'left', padding: '8px', fontWeight: 600, color: '#333' }}>{t('leave_type', 'Leave Type')}</th>
                        <th style={{ textAlign: 'center', padding: '8px', fontWeight: 600, color: '#333' }}>{t('year', 'Year')}</th>
                        <th style={{ textAlign: 'center', padding: '8px', fontWeight: 600, color: '#333' }}>{t('entitlement', 'Entitlement')}</th>
                        <th style={{ textAlign: 'center', padding: '8px', fontWeight: 600, color: '#333' }}>{t('taken', 'Taken')}</th>
                        <th style={{ textAlign: 'center', padding: '8px', fontWeight: 600, color: '#333' }}>{t('remaining', 'Remaining')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaveEntitlements.map((entitlement, idx) => {
                        const entitlementDays = Number(entitlement.entitlement_days || 0);
                        const leaveTypeName = entitlement.leave_type?.name || entitlement.leave_type || '';
                        const key = `${leaveTypeName}-${entitlement.year}`;
                        const serverTaken = typeof entitlement.taken === 'number' ? Number(entitlement.taken) : undefined;
                        const takenFromRequests = takenMap[key];
                        const takenDays = typeof serverTaken === 'number' ? serverTaken : (typeof takenFromRequests === 'number' ? takenFromRequests : 0);
                        const remainingDays = Math.max(0, entitlementDays - takenDays);

                        return (
                          <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                            <td style={{ padding: '8px', color: '#555' }}>{entitlement.leave_type?.name || 'N/A'}</td>
                            <td style={{ textAlign: 'center', padding: '8px', color: '#555' }}>{entitlement.year}</td>
                            <td style={{ textAlign: 'center', padding: '8px', color: '#555', fontWeight: 500 }}>{entitlementDays}</td>
                            <td style={{ textAlign: 'center', padding: '8px', color: '#f44336', fontWeight: 500 }}>{Math.max(0, takenDays)}</td>
                            <td style={{
                              textAlign: 'center',
                              padding: '8px',
                              fontWeight: 600,
                              color: remainingDays > 5 ? '#4caf50' : remainingDays > 0 ? '#ff9800' : '#f44336'
                            }}>
                              {remainingDays}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </li>
          )}

          <li className="w_1_3">
            <div className="formInpt">
              <label className="mb_10">{t('leave_type', 'Leave Type')}</label>
              <select
                value={formData.leave_type_id}
                onChange={(e) => setFormData({ ...formData, leave_type_id: e.target.value })}
                required
              >
                <option value="">Select Leave Type</option>
                {leaveTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>
          </li>

          <li className="w_1_3">
            <div className="formInpt hasDatePicker">
              <label className="mb_10">{t('from', 'From')}</label>
              <input
                name="apply_leave_start_date"
                type="text"
                className="customDatepicker"
                placeholder={t('select_date', 'Select Date')}
                onChange={(e) => handleDateChange('apply_leave_start_date', e.target.value)}
              />
            </div>
          </li>

          <li className="w_1_3">
            <div className="formInpt hasDatePicker">
              <label className="mb_10">{t('to', 'To')}</label>
              <input
                name="apply_leave_end_date"
                type="text"
                className="customDatepicker"
                placeholder={t('select_date', 'Select Date')}
                onChange={(e) => handleDateChange('apply_leave_end_date', e.target.value)}
              />
            </div>
          </li>

          {calculatedDuration && (
            <li className="w_1_1" style={{ paddingTop: '0', paddingBottom: '8px' }}>
              <div style={{
                padding: '10px 14px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: calculatedDuration.isValid ? '#e8f5e9' : '#ffebee',
                color: calculatedDuration.isValid ? '#2e7d32' : '#c62828',
                border: `1px solid ${calculatedDuration.isValid ? '#a5d6a7' : '#ef9a9a'}`
              }}>
                <span style={{ fontSize: '14px' }}>
                  {calculatedDuration.isValid ? (
                    <i className="fa-solid fa-calendar-days" style={{ color: '#2e7d32' }}></i>
                  ) : (
                    <i className="fa-solid fa-triangle-exclamation" style={{ color: '#c62828' }}></i>
                  )}
                </span>
                <span>{calculatedDuration.message}</span>
              </div>
            </li>
          )}

          <li className="w_1_1">
            <div className="formInpt">
              <label className="mb_10">{t('reason', 'Reason')}</label>
              <textarea
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                rows="3"
                required
              />
            </div>
          </li>

          <li className="w_1_1">
            <div className="formInpt">
              <label className="mb_10">{t('supporting_document', 'Supporting Document (Optional)')}</label>
              <input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                onChange={handleFileChange}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: '#fff',
                  fontSize: '13px'
                }}
              />
              {selectedFileName && (
                <div style={{ marginTop: '6px', fontSize: '12px', color: '#1a73e8', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <i className="fa-solid fa-paperclip"></i> Attached: {selectedFileName}
                </div>
              )}
            </div>
          </li>
        </ul>

        <div className="dFlex mt_16 btnSet">
          <button type="submit" className="btn btnPrimaryUI" disabled={loading || (calculatedDuration && !calculatedDuration.isValid)}>
            {loading ? (t('submitting', 'Submitting...')) : (t('submit_leave_request', 'Submit Leave Request'))}
          </button>
          <button type="button" className="btn btnSecondaryUI" onClick={resetForm}>
            {t('clear', 'Clear')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminApplyLeave;
